/** The panels, for whatever range the page is asking about. */

import { Caption1, makeStyles, mergeClasses, tokens } from '@fluentui/react-components'
import { EmptyState, LoadingState, SectionCard } from '../platform'
import type { OverviewSeries } from '../api'
import { HoverProvider, TimeChart } from './Charts'
import {
  OUTCOME_ORDER,
  SETTLEMENT_ORDER,
  laneColour,
  ordered,
  outcomeColour,
  useChartPalette,
} from './chartPalette'
import { orphanReading } from './orphanSeriesReading'
import { settlementReading } from './settlementSeriesReading'

const useStyles = makeStyles({
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    transitionProperty: 'opacity',
    transitionDuration: tokens.durationNormal,
  },
  // Held at reduced opacity while a new range loads, rather than replaced by a
  // spinner: the layout does not jump and the reader keeps their place.
  refetching: {
    opacity: 0.55,
  },
  header: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalM,
    color: tokens.colorNeutralForeground3,
  },
  // Two columns where there is room for two readable charts, one where there
  // is not. The first version stacked every chart at the full width of the
  // page, which is how a 24 hour chart came to be 1,100 px wide and 400 tall.
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 460px), 1fr))',
    gap: tokens.spacingVerticalL,
    alignItems: 'start',
  },
  note: {
    color: tokens.colorNeutralForeground3,
    display: 'block',
  },
  subchart: {
    marginTop: tokens.spacingVerticalS,
  },
  subtitle: {
    color: tokens.colorNeutralForeground2,
    display: 'block',
    marginBottom: tokens.spacingVerticalXXS,
  },
})

const count = (value: number) => value.toLocaleString('en-GB')
const ms = (value: number) => `${Math.round(value).toLocaleString('en-GB')} ms`
const share = (value: number) => `${Math.round(value * 100)}%`

/** The period on screen, as the header states it. */
export function periodLabel(from: string, to: string, zone: string): string {
  const format = (moment: string) =>
    new Date(moment).toLocaleString('en-GB', {
      timeZone: zone,
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    })
  return `${format(from)} to ${format(to)}`
}

/**
 * What the latency chart says about the window when the window does not fit.
 *
 * Drawing the 3,000 ms line put the axis at 3,000 and squashed every
 * percentile into the bottom few pixels, so the chart said nothing except that
 * the window is far away. It is far away: that is a sentence, and the axis goes
 * back to showing the data.
 */
export function windowReading(p99: Array<number | null>, windowMs: number): string {
  const present = p99.filter((value): value is number => value !== null)
  if (present.length === 0) return `Nothing was measured in this range. The OCPP Server allows ${count(windowMs)} ms end to end.`
  const worst = Math.max(...present)
  const used = Math.round((worst / windowMs) * 100)
  return `The slowest p99 in this range was ${ms(worst)}, ${used}% of the ${count(windowMs)} ms the OCPP Server allows end to end.`
}

export default function OverviewGraphs({
  series,
  error,
  loading = false,
  onZoom,
}: {
  series: OverviewSeries | null
  error: string | null
  loading?: boolean
  onZoom?: (from: string, to: string) => void
}) {
  const styles = useStyles()
  const palette = useChartPalette()

  if (error && !series) {
    return (
      <SectionCard title="Over time" description="">
        <EmptyState title="The graphs cannot be drawn" description={error} />
      </SectionCard>
    )
  }
  if (!series) {
    return (
      <SectionCard title="Over time" description="">
        <LoadingState label="Reading the range" />
      </SectionCard>
    )
  }

  const common = {
    buckets: series.buckets,
    bucketSeconds: series.bucket_seconds,
    zone: series.zone,
    onSelect: onZoom,
  }
  const totals = series.buckets.map((_moment, index) =>
    Object.values(series.decisions.by_outcome).reduce((sum, values) => sum + (values[index] ?? 0), 0),
  )
  const outcomes = ordered(Object.keys(series.decisions.by_outcome), OUTCOME_ORDER)
  const lanes = Object.keys(series.lanes).sort()
  const settlements = ordered(Object.keys(series.settlements?.by_outcome ?? {}), SETTLEMENT_ORDER)
  const orphanTotals = series.buckets.map((_moment, index) =>
    Object.values(series.orphans.by_operation).reduce((sum, values) => sum + (values[index] ?? 0), 0),
  )
  const unaskable = series.orphans.not_searchable
  const lookable = orphanTotals.map((total, index) => Math.max(0, total - (unaskable[index] ?? 0)))
  // The window line is drawn only when the tail comes near it. Drawn always, it
  // sets the axis at 3,000 ms and flattens every percentile onto the floor.
  const windowFar = series.latency.p99.every(
    (value) => value === null || value <= series.latency.window_ms * 0.6,
  )

  return (
    <HoverProvider>
      <section
        className={mergeClasses(styles.section, loading && styles.refetching)}
        aria-busy={loading}
        aria-label="Over time"
      >
        <div className={styles.header}>
          <Caption1>{`${periodLabel(series.from, series.to, series.zone)}, ${series.bucket_label}, times in ${series.zone}.`}</Caption1>
          {onZoom ? <Caption1>Drag across any chart to zoom the whole page to that span.</Caption1> : null}
        </div>

        <div className={styles.grid}>
          <SectionCard
            title="Decisions over time"
            description="Every authorization, by what was decided. Refusals sit on top, where the eye lands."
          >
            <TimeChart
              {...common}
              title="Decisions per bucket, stacked by outcome"
              bars={outcomes.map((outcome) => ({
                key: outcome,
                name: outcome,
                colour: outcomeColour(palette, outcome),
                values: series.decisions.by_outcome[outcome],
              }))}
              format={count}
              extra={(index) => {
                const rate = series.decisions.refusal_rate[index]
                return totals[index] === 0
                  ? 'Nothing was decided in this hour.'
                  : `${count(totals[index])} in all, ${rate === null ? 'none' : share(rate)} refused`
              }}
            />
            {/* The share refused gets its own axis, 0 to 100 per cent, rather than
                a line drawn over the bars. On the bars' axis it had no scale at
                all, so a night hour with two decisions, both refused, drew a
                line to the top of the chart that read as a spike in volume. */}
            <div className={styles.subchart}>
              <Caption1 className={styles.subtitle}>Share refused</Caption1>
              <TimeChart
                {...common}
                title="Share of decisions refused per bucket"
                height={96}
                scale={{ top: 1, ticks: [0, 0.5, 1] }}
                format={share}
                lines={[
                  {
                    key: 'refused',
                    name: 'refused',
                    colour: palette.refused,
                    values: series.decisions.refusal_rate,
                  },
                ]}
                extra={(index) =>
                  totals[index] === 0
                    ? 'Nothing was decided, so there is no share to show.'
                    : `of ${count(totals[index])} decided`
                }
              />
            </div>
            <Caption1 className={styles.note}>
              {`${count(series.decisions.total)} decisions in this range. An hour with nothing decided has no share rather than a share of zero, so the line breaks there instead of resting on the floor.`}
            </Caption1>
          </SectionCard>

          <SectionCard
            title="How long an answer took"
            description="The median and the two tails. The tail is what times a charge out."
          >
            <TimeChart
              {...common}
              title="Latency percentiles per bucket"
              lines={[
                { key: 'p50', name: 'p50', colour: palette.p50, values: series.latency.p50 },
                { key: 'p95', name: 'p95', colour: palette.p95, values: series.latency.p95 },
                { key: 'p99', name: 'p99', colour: palette.p99, values: series.latency.p99 },
              ]}
              format={ms}
              reference={windowFar ? undefined : { value: series.latency.window_ms, name: 'the OCPP window' }}
            />
            <Caption1 className={styles.note}>
              {windowReading(series.latency.p99, series.latency.window_ms)}
            </Caption1>
          </SectionCard>

          <SectionCard
            title="Which way the traffic went"
            description="Per lane, so a lane going quiet shows. Each lane keeps its colour whatever else is on screen."
          >
            <TimeChart
              {...common}
              title="Decisions per bucket, stacked by lane"
              bars={lanes.map((lane, index) => ({
                key: lane,
                name: lane,
                colour: laneColour(palette, lane, index),
                values: series.lanes[lane],
              }))}
              format={count}
            />
          </SectionCard>

          <SectionCard
            title="Sessions that named no stamp"
            description="The grey part carried nothing that could be looked up. It is not a decision that went missing; it is a question we cannot ask."
          >
            <TimeChart
              {...common}
              title="Orphan session events per bucket, by whether they could be looked up"
              bars={[
                { key: 'unaskable', name: 'nothing to look up', colour: palette.unaskable, values: unaskable },
                { key: 'lookable', name: 'could be looked up', colour: palette.lookable, values: lookable },
              ]}
              format={count}
              extra={(index) => {
                const parts = Object.entries(series.orphans.by_operation)
                  .filter(([, values]) => (values[index] ?? 0) > 0)
                  .map(([operation, values]) => `${operation} ${count(values[index] ?? 0)}`)
                return parts.length ? `By operation: ${parts.join(', ')}` : null
              }}
            />
            <Caption1 className={styles.note}>
              {orphanReading(series.orphans.total, series.orphans.total_not_searchable)}
            </Caption1>
          </SectionCard>

          {series.settlements ? (
            <SectionCard
              title="Settlements over time"
              description="What the authority said about each finished session, by when we learned it."
            >
              <TimeChart
                {...common}
                title="Settlement outcomes per bucket, stacked by what the authority said"
                bars={settlements.map((outcome) => ({
                  key: outcome,
                  name: outcome,
                  colour: outcomeColour(palette, outcome),
                  values: series.settlements.by_outcome[outcome],
                }))}
                format={count}
              />
              <Caption1 className={styles.note}>
                {settlementReading(series.settlements.total, series.settlements.recorded_from)}
              </Caption1>
            </SectionCard>
          ) : null}
        </div>
      </section>
    </HoverProvider>
  )
}
